import { CircleHelp, FileText, Sparkles } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  DEFAULT_EXAMPLE_SKILL_DESCRIPTION,
  DEFAULT_EXAMPLE_SKILL_NAME,
  SkillCollectionFileName,
} from "../constants";
import { VENDOR_AGNOSTIC_PROVIDER, type SkillCollectionProviderSelection } from "../skill-presets";
import { buildPathWithinParent } from "../utils/path-utils";

import { CreateFileTypeOption } from "./CreateFileTypeOption";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Textarea } from "@/components/ui/textarea";

type CreateFileType = "skill" | "custom";

interface CreateSkillCollectionFileDialogProps {
  open: boolean;
  parentPath: string | null;
  provider: SkillCollectionProviderSelection;
  onOpenChange: (open: boolean) => void;
  onCreateFile: (path: string, options?: { initialContent?: string }) => void;
  getCreateError: (
    parentPath: string | null,
    itemType: "file" | "folder",
    value: string,
  ) => string | null;
}

const sanitizeFrontmatterValue = (value: string) => value.trim().replace(/\s+/g, " ");

const buildSkillFrontmatter = (
  name: string,
  description: string,
  disableModelInvocation: boolean,
) => `---
name: ${sanitizeFrontmatterValue(name)}
description: ${sanitizeFrontmatterValue(description)}
${disableModelInvocation ? "disable-model-invocation: true\n" : ""}---
`;

const OPENAI_SKILL_METADATA_NOTE = `# Skill Instructions

For OpenAI/Codex skills, keep UX/policy/dependencies in \`agents/openai.yaml\`
instead of adding those fields to SKILL.md frontmatter.
`;
const buildSkillFileContent = (
  name: string,
  description: string,
  disableModelInvocation: boolean,
) => `${buildSkillFrontmatter(name, description, disableModelInvocation)}

${OPENAI_SKILL_METADATA_NOTE}
`;

export const CreateSkillCollectionFileDialog = ({
  open,
  parentPath,
  provider,
  onOpenChange,
  onCreateFile,
  getCreateError,
}: CreateSkillCollectionFileDialogProps) => {
  const [fileType, setFileType] = useState<CreateFileType>("skill");
  const [customFileName, setCustomFileName] = useState("");
  const [skillName, setSkillName] = useState(DEFAULT_EXAMPLE_SKILL_NAME);
  const [skillDescription, setSkillDescription] = useState(DEFAULT_EXAMPLE_SKILL_DESCRIPTION);
  const [manualInvocationOnly, setManualInvocationOnly] = useState(false);
  const defaultFileType: CreateFileType =
    provider === VENDOR_AGNOSTIC_PROVIDER ? "custom" : "skill";

  const customFileNameRef = useRef<HTMLInputElement>(null);
  const skillNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFileType(defaultFileType);
    setCustomFileName("");
    setSkillName(DEFAULT_EXAMPLE_SKILL_NAME);
    setSkillDescription(DEFAULT_EXAMPLE_SKILL_DESCRIPTION);
    setManualInvocationOnly(false);

    requestAnimationFrame(() => {
      if (defaultFileType === "skill") {
        skillNameRef.current?.focus();
        return;
      }

      customFileNameRef.current?.focus();
    });
  }, [defaultFileType, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    requestAnimationFrame(() => {
      if (fileType === "skill") {
        skillNameRef.current?.focus();
        skillNameRef.current?.select();
        return;
      }

      customFileNameRef.current?.focus();
      customFileNameRef.current?.select();
    });
  }, [fileType, open]);

  const targetFileName =
    fileType === "skill" ? SkillCollectionFileName.SkillMd : customFileName.trim();
  const targetPath = buildPathWithinParent(parentPath, targetFileName);
  const skillContent = useMemo(
    () => buildSkillFileContent(skillName, skillDescription, manualInvocationOnly),
    [manualInvocationOnly, skillDescription, skillName],
  );
  const validationError = getCreateError(parentPath, "file", targetFileName);
  let metadataError: string | null = null;
  if (fileType === "skill") {
    if (!skillName.trim()) {
      metadataError = "Skill name is required.";
    } else if (!skillDescription.trim()) {
      metadataError = "Skill description is required.";
    }
  }
  const submitError = validationError ?? metadataError;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submitError) {
      return;
    }

    onCreateFile(targetPath, {
      initialContent: fileType === "skill" ? skillContent : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium text-gray-950">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <FileText className="h-4 w-4" />
            </div>
            Create new file
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">File type</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <CreateFileTypeOption
                title="Skill"
                description="Creates `SKILL.md` with skill name and description."
                icon={<Sparkles className="h-3.5 w-3.5" />}
                selected={fileType === "skill"}
                onClick={() => setFileType("skill")}
              />
              <CreateFileTypeOption
                title="Custom"
                description="Creates a custom file with the given name."
                icon={<FileText className="h-3.5 w-3.5" />}
                selected={fileType === "custom"}
                onClick={() => setFileType("custom")}
              />
            </div>
          </div>

          {fileType === "skill" ? (
            <div className="space-y-3.5">
              <div className="flex items-center gap-2.5">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="skill-name" className="text-sm font-medium text-gray-700">
                    Skill name
                  </Label>
                  <Input
                    ref={skillNameRef}
                    id="skill-name"
                    value={skillName}
                    onChange={event => setSkillName(event.target.value)}
                    placeholder={DEFAULT_EXAMPLE_SKILL_NAME}
                    className="h-10 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="skill-description" className="text-sm font-medium text-gray-700">
                  Description
                </Label>
                <Textarea
                  id="skill-description"
                  value={skillDescription}
                  onChange={event => setSkillDescription(event.target.value)}
                  placeholder={DEFAULT_EXAMPLE_SKILL_DESCRIPTION}
                  autoSize
                  className="min-h-[80px] text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={manualInvocationOnly}
                  onChange={event => setManualInvocationOnly(event.target.checked)}
                />
                <span className="inline-flex items-center gap-1.5">
                  <span>Manual-only invocation</span>
                  <BasicTooltip content="Skill will not auto-trigger and is only used when invoked explicitly.">
                    <span className="inline-flex">
                      <CircleHelp className="h-3.5 w-3.5 text-gray-400" />
                    </span>
                  </BasicTooltip>
                </span>
              </label>
              <div className="rounded-md border bg-gray-50 px-3 py-2.5">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-600">
                  Skill preview
                </div>
                <pre className="max-h-28 overflow-auto whitespace-pre-wrap rounded-sm bg-white px-2 py-1.5 font-mono text-xs leading-5 text-gray-700">
                  {skillContent}
                </pre>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="custom-file-name" className="text-sm font-medium text-gray-700">
                File name
              </Label>
              <Input
                ref={customFileNameRef}
                id="custom-file-name"
                value={customFileName}
                onChange={event => setCustomFileName(event.target.value)}
                placeholder="example.md, script.ts, hook.sh…"
                className="h-10 text-sm"
              />
            </div>
          )}

          {submitError && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="min-w-[120px]">
              Create file
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
